import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import type { Alert, AlertEvaluation, PublicAlertView, TenantAlert } from '@ww/shared';
import { ApiError, api } from '../api';
import WeatherMap from '../components/WeatherMap';
import { LIGHTNING_RETENTION_MIN } from '@ww/shared';
import { DISCLAIMER, Logo } from '../components/HeaderBits';
import { I, Icon, KIND_ICON } from '../lib/icons';
import { kindStyle } from '../lib/status';
import { fmtDateTime } from '../lib/time';

const REFRESH_MS = 60_000;

const box: CSSProperties = {
  padding: '14px 18px',
  background: '#0f1a2d',
  border: '1px solid #1e2c46',
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

/**
 * WeatherMap draws whatever is in a TenantAlert, and only needs the geometry and event
 * for that. The rest of the shape is filled with inert values so the public page can
 * reuse the real map instead of carrying a second, lesser one — and so nothing the
 * public endpoint does not return can appear on it.
 */
function mapAlertFrom(v: PublicAlertView): TenantAlert {
  const alert = {
    _id: v.code,
    nwsId: v.code,
    event: v.event,
    category: v.category,
    severity: v.severity,
    headline: v.headline,
    geometry: v.geometry,
    areaDesc: v.areaDesc,
    expires: v.expires,
    sent: v.sent,
    active: v.active,
    affectedZones: [],
    parameters: {},
  } as unknown as Alert;
  const evaluation = {
    alertId: v.code,
    event: v.event,
    category: v.category,
    affectedAssets: [],
    qualifies: true,
    state: v.active ? 'active' : 'expired',
  } as unknown as AlertEvaluation;
  return { alert, evaluation };
}

export default function PublicAlert() {
  const { code = '' } = useParams();
  const [view, setView] = useState<PublicAlertView | null>(null);
  const [error, setError] = useState<{ msg: string; status: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const v = await api.publicAlert(code);
        if (!cancelled) {
          setView(v);
          setError(null);
        }
      } catch (e) {
        if (!cancelled && !(e instanceof ApiError && e.status === 0 && view)) {
          setError({ msg: e instanceof Error ? e.message : String(e), status: e instanceof ApiError ? e.status : 0 });
        }
      }
    };
    void load();
    // A warning gets extended, shrunk or cancelled while someone is looking at it, and
    // this page has no SSE session to tell it so.
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const mapAlerts = useMemo(() => (view ? [mapAlertFrom(view)] : []), [view]);
  const mapTenant = useMemo(
    () => (view ? { center: view.center, boundary: view.boundary, timezone: view.timezone } : null),
    [view],
  );

  const frame = (children: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse at top, #0f1d36 0%, #070d18 60%)' }}>
      <div
        style={{
          maxWidth: 760,
          margin: '0 auto',
          padding: '20px 16px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Logo />
        {children}
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.45, color: '#93a5c1', textAlign: 'center' }}>{DISCLAIMER}</p>
      </div>
    </div>
  );

  if (error && !view) {
    return frame(
      <div className="err-box">
        {error.status === 404
          ? 'This link is not valid, or it has expired. Check the WeatherWatch dashboard for current alerts.'
          : `Could not load this alert: ${error.msg}`}
      </div>,
    );
  }
  if (!view) return frame(<div className="empty">Loading alert…</div>);

  const k = kindStyle(view.event);
  const tz = view.timezone;

  return frame(
    <>
      {!view.active && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            background: '#16223a',
            border: '1px solid #2b3d5c',
            fontSize: 14,
            color: '#c2cedf',
          }}
        >
          This alert is no longer active. Showing the archived record.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span
          style={{
            width: 54,
            height: 54,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: k.tint,
            flexShrink: 0,
          }}
        >
          <Icon d={KIND_ICON[k.kind]} size={30} style={{ color: k.color }} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <h1 className="cond" style={{ margin: 0, fontSize: 30, fontWeight: 700, color: k.color, lineHeight: 1.1 }}>
            {view.event}
          </h1>
          <div style={{ fontSize: 14, color: '#9fb0c8' }}>
            {view.county} · {view.senderName || 'NWS'}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 1,
          background: '#1e2c46',
          border: '1px solid #1e2c46',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {(
          [
            ['In effect until', fmtDateTime(view.expires, tz)],
            ['Issued', fmtDateTime(view.sent, tz)],
            ['Severity', view.severity || '—'],
          ] as const
        ).map(([label, v]) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 14px', background: '#0f1a2d' }}>
            <span className="lbl" style={{ fontSize: 11 }}>
              {label}
            </span>
            <span style={{ fontSize: 15.5, fontWeight: 600 }}>{v}</span>
          </div>
        ))}
      </div>

      {view.instruction && (
        <div style={{ ...box, background: '#3a1419', border: '1px solid #6e2a31', gap: 6 }}>
          <span className="lbl" style={{ color: '#ffb3b7' }}>
            What to do
          </span>
          <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.45, color: '#fff', whiteSpace: 'pre-line' }}>
            {view.instruction}
          </span>
        </div>
      )}

      <div style={{ position: 'relative', height: 300, background: '#0b1526', border: '1px solid #1e2c46', borderRadius: 10, overflow: 'hidden' }}>
        <WeatherMap
          tenant={mapTenant}
          alerts={mapAlerts}
          assets={[]}
          lightning={view.lightning}
          fitToAlert={view.code}
          highlightAlertId={view.code}
          /*
           * Two things this page has always got wrong for a visitor with no account:
           * /api/radar/frames needs a session, so every load 401'd and showed a "radar
           * degraded" chip to the public; and tapping the warning polygon navigated to
           * /alerts/:id, behind RequireAuth, bouncing them to a sign-in screen and
           * losing the map they were sent to read.
           */
          showRadar={false}
          noAlertNav
          compact
        />
      </div>

      {view.lightning.length > 0 && (
        /*
         * Spelled out in full on this page, not abbreviated the way it is inside the
         * app. Every other surface is read by trained staff who know what a satellite
         * product is; this one is read by whoever the text was forwarded to, on a phone,
         * possibly while deciding whether it is safe to be outside. The one reading that
         * must not survive this paragraph is "lightning struck where that dot is".
         */
        <div style={{ ...box, gap: 6 }}>
          <span className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="ltg-dot" aria-hidden /> Lightning nearby
          </span>
          <span style={{ fontSize: 15, lineHeight: 1.5 }}>
            {view.lightning.length} flash{view.lightning.length === 1 ? '' : 'es'} detected in this area
            in the last {LIGHTNING_RETENTION_MIN} minutes.
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: '#9fb0c8' }}>
            These marks are approximate. They come from a weather satellite that sees lightning from
            orbit to within about 6 miles, and they include flashes inside the clouds that never
            reached the ground — <strong>they are not the exact spots where lightning struck</strong>.
            Treat any lightning in the area as a reason to get inside.
          </span>
        </div>
      )}

      {view.affectedAssets.length > 0 && (
        <div style={box}>
          <span className="lbl">Monitored locations affected</span>
          <span style={{ fontSize: 15, lineHeight: 1.6 }}>{view.affectedAssets.join(' · ')}</span>
        </div>
      )}

      <div style={{ ...box, padding: '16px 18px', gap: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4 }}>{view.headline || view.event}</div>
        <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#d3ddeb', whiteSpace: 'pre-line' }}>
          {view.description || 'No description provided.'}
        </div>
        {view.areaDesc && <div style={{ fontSize: 13, color: '#9fb0c8' }}>{view.areaDesc}</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {view.nwsUrl && (
          <a href={view.nwsUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm hov" style={{ textDecoration: 'none' }}>
            Official NWS product <Icon d={I.ext} size={14} />
          </a>
        )}
        <a href="/" className="btn btn-secondary btn-sm hov" style={{ textDecoration: 'none' }}>
          WeatherWatch sign in
        </a>
      </div>
    </>,
  );
}
